import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Contact } from 'types/creators'
import { SocialAccount } from 'types/influencer'
import inModal from 'utils/hoc/inModal'
import './CampaignApplicationConfirmationModal.scss'

interface CampaignApplicationNegotiationModalProps {
    isActive: boolean
    onClose: () => void
    applicant?: { creator: Contact; socialAccount: SocialAccount }
    gigAmount: number
    languageId?: string
    answers?: Record<string, string | number>
    onApply: (
        languageId?: string,
        answers?: Record<string, string | number>,
        quotedOffer?: string | number
    ) => Promise<void>
}

const CampaignApplicationNegotiationModal: React.FC<CampaignApplicationNegotiationModalProps> = ({
    applicant,
    gigAmount,
    languageId,
    answers,
    onApply,
}) => {
    const [amount, setAmount] = useState<string>(gigAmount > 0 ? String(gigAmount) : '')
    const [isApplying, setIsApplying] = useState(false)

    const amountValue = useMemo(() => Number(amount), [amount])
    const isValidAmount = useMemo(() => amount !== '' && amountValue > 0, [amount, amountValue])

    const apply = async () => {
        if (!isValidAmount) {
            toast.error('Please enter a valid amount')
            return
        }
        setIsApplying(true)
        await onApply(languageId, answers, amountValue)
        setIsApplying(false)
    }

    if (!applicant) return null

    const { socialAccount } = applicant
    const profileImage = (socialAccount.profile_picture_url ?? '').split('=')[0]

    return (
        <div className="campaign-apply-confirmation">
            <div className="campaign-apply-confirmation__amount">
                <span className="campaign-apply-confirmation__amount-copy">
                    Think your content is worth more? Tell the brand your preferred payout.
                </span>

                <div className="campaign-apply-confirmation__amount-field">
                    <label
                        htmlFor="quoted-offer-amount"
                        className="campaign-apply-confirmation__amount-label"
                    >
                        ENTER YOUR AMOUNT
                    </label>
                    <input
                        id="quoted-offer-amount"
                        className="campaign-apply-confirmation__amount-input"
                        type="text"
                        inputMode="numeric"
                        value={amount}
                        placeholder={`Ex. ₹${(gigAmount + 100).toLocaleString('en-IN')}`}
                        onChange={(event) => setAmount(event.target.value.replace(/\D/g, ''))}
                    />
                </div>
            </div>

            <div className="campaign-apply-confirmation__footer">
                <div className="campaign-apply-confirmation__account">
                    <img
                        src={profileImage}
                        alt=""
                        className="campaign-apply-confirmation__avatar"
                        width={50}
                        height={50}
                    />
                    <div>
                        <span className="campaign-apply-confirmation__account-label">
                            Applying using
                        </span>
                        <span className="campaign-apply-confirmation__account-handle">
                            {socialAccount.handle}
                        </span>
                    </div>
                </div>

                <div className="campaign-apply-confirmation__disclaimer">
                    <span className="fw-bold">Disclaimer: </span>
                    <span>Application selection is decided by brand.</span>
                </div>

                <button
                    type="button"
                    className="theme-btn btn-primary campaign-apply-confirmation__submit w-100"
                    disabled={isApplying || !isValidAmount}
                    onClick={apply}
                >
                    {isApplying ? 'Applying...' : 'Apply'}
                </button>
            </div>
        </div>
    )
}

export default inModal(CampaignApplicationNegotiationModal, 'Make an Offer', {
    id: 'campaign-negotiation-modal',
    dialogClassName: 'campaign-apply-modal-dialog',
    modalClassName: 'campaign-apply-modal',
})
